import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import {
  Activity,
  Database,
  ChevronDown,
  Info,
  Network,
  Calendar,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import * as Collapsible from '@radix-ui/react-collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface UsageItemProps {
  icon: React.ElementType;
  label: string;
  current?: number;
  limit: number | string;
  percentage?: number;
}

const UsageItem = ({ icon: Icon, label, current, limit, percentage }: UsageItemProps) => {
  const getColor = (pct?: number) => {
    if (!pct || pct === 0) return 'text-muted-foreground';
    if (pct >= 90) return 'text-red-500';
    if (pct >= 75) return 'text-yellow-500';
    return 'text-purple-500';
  };

  const formatValue = (val: number | string | undefined) => {
    if (val === undefined) return '-';
    if (typeof val === 'string') return val;
    if (val < 0) return '∞';
    return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(val);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 py-1">
            <Icon className={cn("h-3.5 w-3.5", getColor(percentage))} />
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className="ml-auto text-xs font-medium">
              {current !== undefined && `${formatValue(current)}/`}
              {formatValue(limit)}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{label}</p>
            <p className="text-xs text-muted-foreground">
              {current !== undefined && `Current: ${new Intl.NumberFormat().format(current)} / `}
              {typeof limit === 'number' && limit >= 0 
                ? `Limit: ${new Intl.NumberFormat().format(limit)}`
                : typeof limit === 'string' 
                ? limit 
                : 'Unlimited'}
            </p>
            {percentage !== undefined && percentage > 0 && (
              <div className="relative w-full bg-secondary rounded-full h-1.5 mt-2">
                <div
                  className={cn(
                    "absolute left-0 top-0 h-1.5 rounded-full transition-all duration-300",
                    getColor(percentage).replace('text-', 'bg-')
                  )}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface CompactUsageIndicatorProps {
  className?: string;
  debugEndpointsCount?: number;
}

export function CompactUsageIndicator({ className, debugEndpointsCount }: CompactUsageIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: currentPlan, isLoading } = useCurrentPlan();
  
  console.log('CompactUsageIndicator - currentPlan:', currentPlan);
  console.log('CompactUsageIndicator - next_renovation_date:', currentPlan?.next_renovation_date);

  if (isLoading || !currentPlan?.usages) {
    return null;
  }

  const calculatePercentage = (current?: number, limit?: number) => {
    if (current === undefined || limit === undefined) return 0;
    if (limit < 0) return 0; // Unlimited
    if (limit === 0) return current > 0 ? 100 : 0;
    return (current / limit) * 100;
  };

  const debugEndpointUsage =
    currentPlan.usages.debug_endpoints ?? currentPlan.usages.bridges;

  const rawDebugEndpointLimit =
    debugEndpointUsage?.limit ??
    currentPlan.limits?.debug_endpoints ??
    currentPlan.usages.bridges?.limit;

  const normalizedDebugEndpointLimit =
    rawDebugEndpointLimit === undefined
      ? 0
      : rawDebugEndpointLimit === 0
      ? -1
      : rawDebugEndpointLimit;

  const resolvedDebugEndpointCount =
    debugEndpointsCount ?? debugEndpointUsage?.count;

  const dataRetentionLimit = currentPlan.usages.data_retention_days?.limit ?? 0;

  const usageItems = [
    {
      icon: Network,
      label: 'Debugging Endpoints',
      current: resolvedDebugEndpointCount,
      limit: normalizedDebugEndpointLimit,
      percentage: calculatePercentage(
        resolvedDebugEndpointCount,
        normalizedDebugEndpointLimit < 0
          ? undefined
          : normalizedDebugEndpointLimit
      ),
    },
    {
      icon: Database,
      label: 'Retention',
      limit: `${dataRetentionLimit}d`,
    },
  ];

  // Check if any usage is above 75%
  const hasHighUsage = usageItems.some(item => item.percentage && item.percentage >= 75);

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
      <div className={cn("bg-background/30 rounded-lg border border-white/10", className)}>
        <Collapsible.Trigger asChild>
          <button className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/5 transition-colors rounded-lg">
            <div className="flex items-center gap-2">
              <Activity className={cn(
                "h-4 w-4",
                hasHighUsage ? "text-yellow-500" : "text-muted-foreground"
              )} />
              <span className="text-sm font-medium">Usage</span>
              {hasHighUsage && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Info className="h-3 w-3 text-yellow-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Some limits are near capacity</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <ChevronDown 
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </button>
        </Collapsible.Trigger>
        <Collapsible.Content className="overflow-hidden">
          <div className="px-3 pb-2 space-y-0.5">
            {currentPlan.next_renovation_date && !currentPlan.is_canceling && (
              <div className="flex items-center gap-2 py-2 mb-1 border-b border-white/10">
                <Calendar className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs text-muted-foreground">
                  Renews on <span className="font-medium text-foreground">
                    {new Date(currentPlan.next_renovation_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </span>
              </div>
            )}
            {usageItems.map((item) => (
              <UsageItem key={item.label} {...item} />
            ))}
          </div>
        </Collapsible.Content>
      </div>
    </Collapsible.Root>
  );
}
